import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { getGoLockerLocations } from '../../utils/service';
import {
  GOLOCKER_DEFAULT_RADIUS_MILES,
  getLiveHeaderState,
  getLiveTip,
  withUnknownAvailability,
} from '../../utils/goLocker';
import GoLockerMap from './GoLockerMap';
import GoLockerLocationRow from './GoLockerLocationRow';
import GoLockerTip from './GoLockerTip';

import styles from './GoLocker.module.scss';

/**
 * GoLockerLiveLocations — what the selected GoLocker option expands into (Figma G2
 * 12229:3234, E4 12256:5654, E6 12256:6149): map, "GoLockers within 1 mi" + live
 * status, one row per bank, and the tip below.
 *
 * Calls POST /shipping-locations/golocker with the same address the providers call
 * used. Until it lands the rows render from the card's `nearestLocations` (no
 * availability line), so the panel never opens empty. If it fails, those preview
 * rows stay with "Live availability isn't loading" and the header offers Retry —
 * every listed bank is still a valid drop-off.
 *
 * Returns two siblings — the attached panel and the tip — because the design puts
 * the tip OUTSIDE the panel, below it. The caller supplies `panelClassName` for the
 * attached-container chrome it already owns.
 */
const GoLockerLiveLocations = ({ address, presetLocations = [], panelClassName }) => {
  const [state, setState] = useState({
    loading: true,
    error: false,
    locations: presetLocations,
    allFull: false,
    radiusMiles: GOLOCKER_DEFAULT_RADIUS_MILES,
  });
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const load = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: false }));
    const { data, error } = await getGoLockerLocations({ address });
    if (!mountedRef.current) return;
    if (error || !Array.isArray(data?.locations)) {
      // Keep whatever rows we had (preview or a previous answer) but drop their
      // availability: a stale "Open now" after a failed refresh would mislead.
      setState((prev) => ({
        ...prev,
        loading: false,
        error: true,
        allFull: false,
        locations: withUnknownAvailability(
          prev.locations.length ? prev.locations : presetLocations,
        ),
      }));
      return;
    }
    setState({
      loading: false,
      error: false,
      // An empty live answer would blank a card the BE just offered; keep the preview.
      locations: data.locations.length ? data.locations : presetLocations,
      allFull: Boolean(data.allFull),
      radiusMiles: data.radiusMiles ?? GOLOCKER_DEFAULT_RADIUS_MILES,
    });
  }, [address, presetLocations]);

  useEffect(() => {
    load();
  }, []);

  const header = getLiveHeaderState(state);
  const tip = getLiveTip(header.status, !state.loading && state.allFull);

  return (
    <>
      <div className={classNames(styles.panel, panelClassName)}>
        <GoLockerMap locations={state.locations} address={address} />

        <div className={styles.listHeader}>
          <span className={styles.listTitle}>{header.title}</span>
          {header.status === 'live' && (
            <span className={classNames(styles.liveStatus, styles['tone-available'])}>
              <span className={styles.dot} aria-hidden="true" />
              Live · updated just now
            </span>
          )}
          {header.status === 'error' && (
            <span className={classNames(styles.liveStatus, styles['tone-unknown'])}>
              <span className={styles.dot} aria-hidden="true" />
              <span>
                {'Couldn\'t load · '}
                <button
                  type="button"
                  className={styles.retry}
                  onClick={(e) => {
                    // The panel sits under a radio card; don't let Retry re-select it.
                    e.stopPropagation();
                    load();
                  }}
                >
                  Retry
                </button>
              </span>
            </span>
          )}
        </div>

        {state.locations.map((location, index) => (
          <Fragment key={location.id ?? index}>
            {index > 0 && <div className={styles.rowDivider} />}
            <GoLockerLocationRow location={location} position={index + 1} />
          </Fragment>
        ))}
      </div>
      <GoLockerTip className={styles.panelTip}>{tip}</GoLockerTip>
    </>
  );
};

export default GoLockerLiveLocations;
